import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  getCombatProPurchaseHistory, getCombatProTier, getCombatProOwned,
  getCombatProCloudActive, getTierSpec, onCombatProChange, fmtDate,
  COMBAT_TIERS,
} from './combatProTiers';
import { CombatProShell, cps, tierColor } from './CombatProShared';

export default function CombatProPurchaseHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const owned = getCombatProOwned(user?.entitlements?.combat_system_pro);
  const [history, setHistory] = useState(getCombatProPurchaseHistory());
  const [tier, setTier] = useState(getCombatProTier());

  useEffect(() => onCombatProChange(() => {
    setHistory(getCombatProPurchaseHistory());
    setTier(getCombatProTier());
  }), []);

  const cloud = getCombatProCloudActive();
  const spec = getTierSpec(tier);
  const DASH = '\u2014';

  // Account Summary rows (values centralised in combatProTiers.js).
  const summaryRows = [
    { label: 'Combat PRO Ownership', value: owned ? 'Owned \u2713' : 'Not Owned', color: owned ? '#4ade80' : 'rgba(255,255,255,0.7)' },
    { label: 'Current Membership', value: COMBAT_TIERS[tier].name, color: tierColor(tier) },
    { label: 'Current Tier', value: COMBAT_TIERS[tier].name, color: tierColor(tier) },
    { label: 'Cloud Status', value: cloud ? 'Active' : 'Local Only', color: cloud ? '#4ade80' : 'rgba(255,255,255,0.7)' },
    { label: 'Saved Encounter Capacity', value: String(spec.savedEncounters), color: '#fff' },
    { label: 'Renewal Date', value: DASH, color: 'rgba(255,255,255,0.7)' },
  ];

  const emptyLines = [
    'Combat PRO ownership history',
    'Membership changes',
    'Future renewals',
    'Transaction records',
    'Billing activity',
  ];

  return (
    <CombatProShell title="Purchase History" subtitle="Combat PRO ownership and membership activity." navigate={navigate}>

      {/* ── Account Summary ── */}
      <div style={{ ...cps.card, gap: 16 }}>
        <div style={asHeading}>Account Summary</div>
        <div style={asGrid}>
          {summaryRows.map((r, i) => (
            <div key={i} style={asRow}>
              <span style={asLabel}>{r.label}</span>
              <span style={{ ...asValue, color: r.color }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Purchase History ── */}
      <div style={cps.sectionTitle}>Purchase History</div>

      {history.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: 46, lineHeight: 1 }}>{'\uD83D\uDCC4'}</div>
          <div style={emptyTitle}>No Purchases Yet</div>
          <div style={emptyDesc}>Combat PRO purchases and membership transactions will appear here automatically.</div>
          <div style={emptyList}>
            {emptyLines.map((line, i) => (
              <div key={i} style={emptyItem}>
                <span style={emptyDot} />{line}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={cps.tableWrap}>
          <table style={cps.table}>
            <thead>
              <tr>
                <th style={{ ...cps.th, textAlign: 'left' }}>Date</th>
                <th style={{ ...cps.th, textAlign: 'left' }}>Product</th>
                <th style={cps.th}>Price</th>
                <th style={cps.th}>Status</th>
                <th style={cps.th}>Type</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td style={{ ...cps.td, textAlign: 'left' }}>{fmtDate(h.date)}</td>
                  <td style={{ ...cps.td, textAlign: 'left' }}>{h.product}</td>
                  <td style={cps.td}>{h.price}</td>
                  <td style={cps.td}>{h.status}</td>
                  <td style={cps.td}>{h.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoices */}
      <div style={cps.sectionTitle}>Invoices</div>
      <div style={cps.tableWrap}>
        <table style={cps.table}>
          <thead>
            <tr>
              <th style={{ ...cps.th, textAlign: 'left' }}>Invoice Number</th>
              <th style={{ ...cps.th, textAlign: 'left' }}>Date</th>
              <th style={cps.th}>Amount</th>
              <th style={cps.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={{ ...cps.td, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }} colSpan={4}>No invoices yet. Invoices will appear here automatically.</td></tr>
          </tbody>
        </table>
      </div>

      {/* Subscription History */}
      <div style={cps.sectionTitle}>Subscription History</div>
      <div style={cps.tableWrap}>
        <table style={cps.table}>
          <thead>
            <tr>
              <th style={{ ...cps.th, textAlign: 'left' }}>Event</th>
              <th style={{ ...cps.th, textAlign: 'left' }}>Date</th>
              <th style={cps.th}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {[['Tier Changes', DASH], ['Renewals', DASH], ['Cancellations', DASH]].map(([label, val], i) => (
              <tr key={i}>
                <td style={{ ...cps.td, textAlign: 'left' }}>{label}</td>
                <td style={{ ...cps.td, textAlign: 'left' }}>{val}</td>
                <td style={cps.td}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Billing Notes */}
      <div style={cps.sectionTitle}>Billing Notes</div>
      <div style={{ ...cps.card }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.55 }}>
          <div>Ownership purchases are one-time payments that grant permanent Combat PRO access.</div>
          <div>Memberships are recurring monthly subscriptions that add cloud features.</div>
          <div>Renewals occur automatically each billing cycle while a membership is active.</div>
          <div>Cancellations stop future renewals. Combat PRO ownership is always retained.</div>
        </div>
      </div>
    </CombatProShell>
  );
}

// ── Local styles (match the dark VISINVOKE card language) ──
const asHeading = { fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: 0.2 };

const asGrid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '4px 30px' };

const asRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' };

const asLabel = { fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 600 };

const asValue = { fontSize: 14.5, fontWeight: 800, textAlign: 'right' };

const emptyCard = {
  background: 'rgba(30,24,70,0.88)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 16,
  padding: '56px 32px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 14,
};

const emptyTitle = { fontSize: 22, fontWeight: 800, color: '#fff' };

const emptyDesc = { fontSize: 14.5, color: 'rgba(255,255,255,0.62)', maxWidth: 520, lineHeight: 1.5 };

const emptyList = { display: 'flex', flexDirection: 'column', gap: 9, marginTop: 10 };

const emptyItem = { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'rgba(255,255,255,0.7)', fontWeight: 600 };

const emptyDot = { width: 6, height: 6, borderRadius: '50%', background: '#fbbf24', flexShrink: 0, display: 'inline-block' };
